import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Split a list into fixed-size chunks.
 *
 * Fixed size is the load-bearing part, not an implementation detail: each chunk
 * owns its own batched photo fetch, and React Query keys those fetches on the
 * chunk's id list. Growing a single window instead (`items.slice(0, visible)`)
 * would mint a brand-new cache key on every growth step and re-fetch — and
 * re-sign — everything already loaded. Fixed chunks keep every already-mounted
 * chunk's key byte-identical, so revealing more only ever fetches the new one.
 */
export function chunkArray<T>(items: readonly T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    throw new Error('chunkArray: chunkSize must be greater than zero');
  }

  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}

/** How far below the viewport the sentinel starts pulling the next chunk in. */
const REVEAL_ROOT_MARGIN = '600px';

export interface ChunkedReveal {
  /** How many chunks to render right now (always ≥1, never above `totalChunks`). */
  visibleChunks: number;
  /** Attach to an element rendered after the last visible chunk. */
  sentinelRef: (node: HTMLElement | null) => void;
  /** Whether any chunks remain hidden. */
  hasMore: boolean;
}

/**
 * Reveal a chunked list incrementally as the user scrolls.
 *
 * Why incremental reveal rather than rendering everything: the members
 * directory renders ~950 cards, each pulling a signed thumbnail, so a full
 * render costs a roster-sized DOM and a roster-sized signed-URL fan-out to show
 * the dozen cards that are actually on screen.
 *
 * `resetKey` — NOT the item array — is what rewinds the window. Resetting on
 * the array's identity would also rewind on a background refetch that returned
 * the very same members, yanking the user back to the top of a list they had
 * scrolled through. Callers pass whatever genuinely means "this is a different
 * list now" (here, the membership status filter).
 */
export function useChunkedReveal(totalChunks: number, resetKey: unknown): ChunkedReveal {
  const [revealed, setRevealed] = useState(1);
  const [lastResetKey, setLastResetKey] = useState(resetKey);

  // Adjusted during render, not in a passive effect. An effect rewinds one
  // render too late: the first render after a filter change would still be
  // holding the previous window, so N chunks of the NEW list would mount and
  // each fire its photo fetches before the reset unmounted them again. Someone
  // deep-scrolled switching filters would kick off exactly the burst of
  // signed-URL requests this whole change exists to prevent.
  //
  // Setting state during render of the *same* component is the supported React
  // pattern for this: the re-render happens before children render and before
  // effects run, so no child ever observes the stale window.
  if (!Object.is(resetKey, lastResetKey)) {
    setLastResetKey(resetKey);
    setRevealed(1);
  }

  // Clamped rather than stored clamped: a filter change that shrinks the list
  // must not leave `revealed` pointing past the end, and a list that grows
  // again should not silently keep a stale larger window.
  const visibleChunks = Math.min(Math.max(revealed, 1), Math.max(totalChunks, 1));
  const hasMore = visibleChunks < totalChunks;

  const observerRef = useRef<IntersectionObserver | null>(null);
  // Read inside the observer callback so the observer never has to be rebuilt
  // (and re-fire) just because the totals changed. Assigned in a commit-phase
  // effect rather than during render — a render-phase write is an impure render.
  // A momentarily stale value only under-reveals for one pass: the sentinel is
  // still mounted (`hasMore` reads the clamped value above, not this ref), so it
  // fires again on the next intersection and catches up.
  const totalChunksRef = useRef(totalChunks);
  useEffect(() => {
    totalChunksRef.current = totalChunks;
  }, [totalChunks]);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) return;

    // Older Safari (the reception iMac runs 10.15) predates
    // IntersectionObserver; without it the sentinel would never fire and the
    // list would be stuck at one chunk, so reveal everything instead.
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(totalChunksRef.current || 1);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        // Keeps firing while the sentinel stays in view, which is what a short
        // list in a tall window needs; it terminates at the chunk count.
        setRevealed((current) => Math.min(current + 1, totalChunksRef.current));
      },
      { rootMargin: REVEAL_ROOT_MARGIN },
    );

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return useMemo(
    () => ({ visibleChunks, sentinelRef, hasMore }),
    [visibleChunks, sentinelRef, hasMore],
  );
}
